import React, { useState, useEffect, useRef, useCallback } from 'react';
import CastOffer from '../Notes/CastOffer';
import ChatIdeaOffers from './ChatIdeaOffers';
import { groupCardOffers } from '../../utils/chatIdeaOffers';
import BringSeriesPeopleOffer from '../Notes/BringSeriesPeopleOffer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { mergePathGapOpener } from '../../utils/pathGapOpener';
import { messageAnchorId } from '../../utils/searchMessageTarget';
import {
  CHAT_TOOLS,
  HELP_EXPLAIN,
  HELP_GET_STARTED,
  HELP_JUST_LISTEN,
  HELP_SMALL_HINT,
  SERIES_BOOK_CHAT_NOTE,
  STEP0_ANSWER_PROMPT,
  STEP0_NO,
  STEP0_QUESTION,
  STEP0_YES,
} from '../../utils/writerCopy';
import { shouldOfferStep0 } from '../../utils/step0Opening';
import {
  mergeServerThread,
  shouldApplyChatMessageLoad,
} from '../../utils/applyChatMessageLoad';
import { asChatText } from '../../utils/streamingReplyDisplay';
import { chatApi } from '../../api/chat';
import { storyBibleApi } from '../../api/storyBible';
import { bookmarksApi } from '../../api/bookmarks';
import { sessionsApi } from '../../api/sessions';
import { plotDossiersApi } from '../../api/plotDossiers';
import { withChatLoadRetry } from '../../utils/loadChatSession';
import { settingsApi } from '../../api/settings';
import { entitiesApi } from '../../api/entities';
import { useApp } from '../../context/AppContext';
import ChatModeLine from './ChatModeLine';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import SessionList from '../Sessions/SessionList';
import Loading from '../Common/Loading';
import ErrorMessage from '../Common/ErrorMessage';
import ChatErrorBoundary from './ChatErrorBoundary';
import SummaryStatusChip from './SummaryStatusChip';
import CreateSummaryButton from './CreateSummaryButton';
import ContinuationModal from './ContinuationModal';
import SessionSummaryModal from './SessionSummaryModal';
import Toast from '../Common/Toast';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { CHAT_TEXT_SIZE, readChatZoom } from '../../utils/textSizeRange';
import SettingsDialog from '../Settings/SettingsDialog';
import NarrativePovStrip from '../NarrativePov/NarrativePovSettle';
import LoglineHeader from './LoglineHeader';
import LoglineSessionHeader from '../Session/LoglineSessionHeader';
import StoryNavigatorBadge from './StoryNavigatorBadge';
import { usersApi } from '../../api/users';
import ModelPicker from '../Common/ModelPicker';
import ContentModerationAlert from './ContentModerationAlert';
import ConnectSceneModal from '../Session/ConnectSceneModal';
import NewSessionDialog from '../Session/NewSessionDialog';
import { markSeriesFocusOfferDeclined } from './SeriesFocusOfferCard';
import { markPlotReadyOfferDeclined } from './PlotReadyOfferCard';
import { plotReadyOfferPath } from '../../utils/pathRoomPath';
import { attachBeatWorkshopOffer } from './BeatWorkshopOfferCard';
import SequencePreviewPanel from '../Focus/SequencePreviewPanel';
import PlotFocusColumn from '../Focus/PlotFocusColumn';
import { persistAcceptedBookIdea } from '../../utils/seriesOffers';
import { seriesGetIdeasApi } from '../../api/seriesGetIdeas';
import SeriesShapeChooser from '../Series/SeriesShapeChooser';
import { SERIES_PLANNING_TOOLS } from '../../utils/seriesBeta';
import AddBookModal from '../Projects/AddBookModal';
import {
  DISCOVERY_SHAPE_LEDE,
  shouldShowSeriesShapeChooser,
} from '../../utils/seriesGetIdeas';
import FrameworkPicker from '../Frameworks/FrameworkPicker';
import FrameworkReferencePanel from '../Focus/FrameworkReferencePanel';
import { parseStoryFrameworkRef, storyFrameworkRef } from '../../api/frameworks';
import { workshopsApi } from '../../api/workshops';
import { useKeyboardOffset } from '../../hooks/useKeyboardOffset';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import PaidActionConfirm from '../Common/PaidActionConfirm';
import PaidActionEstimate from '../Common/PaidActionEstimate';
import ChatPaneFrame from './ChatPaneFrame';
import { ROOM_CHAT_EMPTY } from '../../utils/roomChat';
import { IDEA_ACTION_EVENT } from './ideaRoom';
import ChatAttachmentBar from './ChatAttachmentBar';
import { unwrapSettings } from '../../utils/chatSpend';
import {
  ACCOUNT_READY_MESSAGE,
  BETA_ALLOWANCE_USED_MESSAGE,
  composerBlockedCopy,
  spendAllowed,
  creditsErrorCopy,
  REPLY_DID_NOT_ARRIVE_MESSAGE,
  REPLY_STOPPED_EARLY_MESSAGE,
  shouldPollKeyProvisioning,
} from '../../utils/betaAllowance';
import { betaAiPaused } from '../../utils/betaSpendPause';
import { openSettingsAtApiKey } from '../../utils/openSettings';
import { persistedComposerEcho } from '../../utils/composerDraft';
import { unreachableSend } from '../../utils/unreachableSend';
import {
  REPLY_FINISH_CAP_MS,
  REPLY_POLL_MS,
  REPLY_RECOVER_MS,
  forgetReplyWait,
  mutedSilenceTail,
  readReplyWait,
  recoveredAssistant,
  rememberReplyWait,
  replyGapForThread,
  replyInFlightConflict,
  replyRecoverRemainingMs,
  sendLocked,
  withoutEmptyReplyWait,
} from '../../utils/replyRecovery';
import ReplyFollow from './ReplyFollow';
import { PAID_ACTIONS, threadInputTokens } from '../../utils/estimatePaidActionCost';
import './Chat.css';

// Empty Discovery first-session guidance (#942). Hardcoded system text,
// not an LLM turn (Hardcoded Message Rule). Book or series Discovery
// only. Both lines leave after the first writer message. Does not turn
// Teach on. Does not change Jump Start or Nudge behavior.
export const DISCOVERY_EMPTY_SWITCH_HINT =
  'This is where you work the story out. Mute keeps replies off while you write. Teach explains craft words. Jump Start gives one prompt when you’re stuck. After you’ve started, Nudge offers a few directions to react to.';

export const DISCOVERY_EMPTY_INVITATION =
  'What’s the story you would like to work on? Start anywhere — a person, a place, a problem, or a rough idea of what happens.';

// Discovery chrome button hover titles (#958). Joel locked 2026-09-04.
export const DISCOVERY_CHROME_TITLES = {
  muteOff: 'Mute — keep writing without replies; the partner takes notes',
  muteOn: 'Unmute — resume conversation',
  teachOff: 'Teach — explain craft words (like Act, Scene, stakes) as they come up',
  teachOn: 'Teach is on — turn off to stop teaching',
  jumpStart: "Jump Start — one contextual prompt when you're stuck (empty Discovery or mid-chat)",
  jumpStartMuted: 'Unavailable while muted',
  nudge: "Nudge — a few concrete directions to react to (appears after you've started)",
};

// Retired empty Discovery openers. Do not show these as the empty
// Discovery first line.
const RETIRED_DISCOVERY_EMPTY_OPENER =
  "What's the most important thing you're bringing";

// #1341: same bar as the server's Your cast offer (three things said).
const CAST_MIN_WRITER_TURNS = 3;
function writerTurnCount(messages) {
  return (messages || []).filter((m) => {
    const who = String(m?.speaker || m?.role || '').toLowerCase();
    return (who === 'user' || who === 'writer') && String(m?.content || '').trim();
  }).length;
}

/** Server bookmark rows keyed by the message they pin. */
export function bookmarkIdsByMessage(rows) {
  const seeded = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.message_id && row?.id) seeded[String(row.message_id)] = row.id;
  });
  return seeded;
}

/** Pane title names the room. Past chats holds the session name. */
export function chatPaneTitleSubject({ subject, pane, pinned = false }) {
  if (subject) return subject;
  if (pinned || pane) return 'this session';
  return 'Idea';
}

const ChatInterface = ({
  sessionId,
  pane = false,
  subject,
  preferSubject = false,
  headerModel = true,
  attachment = null,
  onClearAttachment = null,
  roomKey = null,
  pinSession = false,
  onPinnedSession = null,
  focusSectionId = null,
}) => {
  const {
    currentProject,
    setCurrentProject,
    currentStory,
    currentSession,
    navigateToSession,
    getPreviousSessionId,
    clearPreviousSession,
    clearCurrentSession,
    updateSessionStatus,
    updateCurrentSession,
    workingStyle,
    setLastDebugData
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  useKeyboardOffset();
  const breakpoint = useBreakpoint();
  const phonePaneControls = pane && breakpoint === 'mobile';
  const isPhone = breakpoint === 'mobile';
  const [chatToolsOpen, setChatToolsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [replyInFlight, setReplyInFlight] = useState(null);
  const [replyWatch, setReplyWatch] = useState(0);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  // Spur Creek Two Step: a sitting with a session id is not empty until
  // the server thread has been asked for. Side Door return remounts
  // this talk; an empty first paint looks like the sitting vanished.
  const [isLoading, setIsLoading] = useState(!!sessionId);
  const [paneLoadedId, setPaneLoadedId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  // Set only after the writer sends. The composer may remount while the
  // reply is still arriving (an empty room creates its session on send).
  const [restoreComposerFocus, setRestoreComposerFocus] = useState(false);
  const [sendMissed, setSendMissed] = useState(false);
  // Drops stale empty GET-after-open loads so they cannot wipe a landed
  // streamed reply or flip the thread back to a full-page spinner (#947).
  // A remount GET that still has history must paint even if Jump Start
  // / Nudge bumped the generation while the GET was out (#959).
  const loadGenerationRef = useRef(0);
  const threadLiveRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  const messagesRef = useRef(messages);
  const stallHandledRef = useRef(false);
  const composerLockedRef = useRef(false);
  const sendAttemptRef = useRef(0);
  const sendingRef = useRef(false);
  const seatedSessionRef = useRef(null);
  const pendingPaneSendRef = useRef(null);
  const pendingPaneTempRef = useRef(null);
  const sendPaneRef = useRef(null);
  // #2580: the offers row sits below the list and shrinks it when it appears
  // or grows. If the writer was at the bottom, keep them there. A callback ref
  // so the observer attaches whenever the row mounts (not only on first render).
  const noteOffersObserverRef = useRef(null);
  const noteOffersRef = useCallback((row) => {
    if (noteOffersObserverRef.current) {
      noteOffersObserverRef.current.disconnect();
      noteOffersObserverRef.current = null;
    }
    if (!row || typeof ResizeObserver === 'undefined') return;
    let lastHeight = row.offsetHeight;
    const observer = new ResizeObserver(() => {
      const height = row.offsetHeight;
      const grew = Math.max(0, height - lastHeight);
      lastHeight = height;
      const list = (row.parentElement && row.parentElement.querySelector('.message-list'))
        || document.querySelector('.message-list');
      if (!list) return;
      // The list already shrank by `grew`, so allow for it when asking "was at the bottom".
      if (list.scrollHeight - list.scrollTop - list.clientHeight <= 48 + grew) {
        list.scrollTop = list.scrollHeight;
      }
    });
    observer.observe(row);
    noteOffersObserverRef.current = observer;
  }, []);
  sessionIdRef.current = sessionId;
  messagesRef.current = messages;
  sendingRef.current = isSending;
  const [error, setError] = useState(null);
  const [sendNotice, setSendNotice] = useState(null);
  const [citationMap, setCitationMap] = useState({});
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  // History pagination (issue #202): load the tail on open, lazy-load older
  // batches on scroll-up via the keyset cursor.
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  // Refs stay current across an awaited page loop. State updates from one
  // page would still be stale inside the next call.
  const hasMoreMessagesRef = useRef(false);
  const messagesCursorRef = useRef(null);
  const olderMessagesInFlightRef = useRef(false);
  const setMessagePage = (hasMore, cursor) => {
    const nextCursor = cursor || null;
    hasMoreMessagesRef.current = !!hasMore;
    messagesCursorRef.current = nextCursor;
    setHasMoreMessages(!!hasMore);
    setMessagesCursor(nextCursor);
  };

  // Discovery suggestion state (from last API response)
  const [pendingFoundation, setPendingFoundation] = useState(null);
  const [lastAssistantMsgId, setLastAssistantMsgId] = useState(null);

  // Session Summary Service state
  const [toast, setToast] = useState(null);
  const [showContinuationModal, setShowContinuationModal] = useState(false);
  const [continuationInfo, setContinuationInfo] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [attachStatus, setAttachStatus] = useState('idle');
  useEffect(() => { setAttachStatus('idle'); }, [attachment?.id, attachment?.kind]);
  useEffect(() => {
    if (pane) return undefined;
    const onIdea = (event) => {
      if (event.detail === 'summary') setShowSummaryModal(true);
    };
    window.addEventListener(IDEA_ACTION_EVENT, onIdea);
    return () => window.removeEventListener(IDEA_ACTION_EVENT, onIdea);
  }, [pane]);
  // When the manual "Create Summary" button is clicked over a writer_edited
  // head, open the summary modal straight into the merge-draft review.
  const [summaryAutoMerge, setSummaryAutoMerge] = useState(false);

  // Summary status chip — passive indicator for interim summary
  // generation (manual button and automatic threshold). { status, message }
  // or null when hidden. Timers live in a ref so session switches and
  // dismissal can cancel pending auto-clear / completion polling.
  const [summaryStatus, setSummaryStatus] = useState(null);
  const summaryTimersRef = useRef({ clear: null, poll: null });

  // Track bookmarked messages: { messageId: bookmarkId }
  const [bookmarkedMessages, setBookmarkedMessages] = useState({});
  const bookmarkTouchRef = useRef(0);
  const bookmarkFlightRef = useRef(false);

  // Pins are a local map seeded from the server list. The panel delete
  // used to leave that map in place, so the pin stayed highlighted.
  // A reload read the same list; without no-store a cached copy brought
  // the pin back after the bookmark was already gone.
  useEffect(() => {
    const storyId = currentStory?.id;
    if (!storyId) return undefined;
    let cancelled = false;

    const applyRows = (rows, touchedAtStart) => {
      if (cancelled || bookmarkTouchRef.current !== touchedAtStart) return;
      const next = bookmarkIdsByMessage(rows);
      setBookmarkedMessages((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (
          prevKeys.length === nextKeys.length
          && nextKeys.every((key) => prev[key] === next[key])
        ) {
          return prev;
        }
        return next;
      });
    };

    const loadPins = (touchedAtStart) => {
      bookmarksApi.list(storyId, null, { cache: 'no-store' })
        .then((rows) => applyRows(rows, touchedAtStart))
        .catch(() => {
          /* the pin the writer just set stays; the list can load later */
        });
    };

    loadPins(bookmarkTouchRef.current);

    const onRemoved = (event) => {
      const bookmarkId = event?.detail?.bookmarkId;
      if (bookmarkId != null) {
        const dropped = String(bookmarkId);
        setBookmarkedMessages((prev) => {
          let changed = false;
          const next = {};
          Object.entries(prev).forEach(([messageId, id]) => {
            if (String(id) === dropped) {
              changed = true;
              return;
            }
            next[messageId] = id;
          });
          return changed ? next : prev;
        });
      }
      bookmarkTouchRef.current += 1;
      loadPins(bookmarkTouchRef.current);
    };

    window.addEventListener('bookmark-removed', onRemoved);
    return () => {
      cancelled = true;
      window.removeEventListener('bookmark-removed', onRemoved);
    };
  }, [currentStory?.id]);

  // RE-ACTIVATED: GuidanceMode for Session Modes feature (January 2026)
  const [guidanceMode, setGuidanceMode] = useState('explore');
  const [focusTemplate, setFocusTemplate] = useState(null);
  const [focusTargetName, setFocusTargetName] = useState('');
  const [isModeUpdating, setIsModeUpdating] = useState(false);

  // Logline state for focus mode + logline template
  const [currentLogline, setCurrentLogline] = useState(null);

  // Mute (mute-button-spec.md): dictation-only state — messages persist,
  // no response generates. Seeded from session data on load, toggled via
  // the session-update path (PATCH /sessions/{id}).
  const [muted, setMuted] = useState(false);
  const [isMuteUpdating, setIsMuteUpdating] = useState(false);
  // Teach and Mute share one-choice state, on Idea and in every room.
  const [teach, setTeach] = useState(false);
  const [isTeachUpdating, setIsTeachUpdating] = useState(false);
  const [sessionType, setSessionType] = useState(currentSession?.session_type || null);
  const [step0Asked, setStep0Asked] = useState(false);
  const [step0Typing, setStep0Typing] = useState(false);
  const [step0Focus, setStep0Focus] = useState(0);
  const [step0Facts, setStep0Facts] = useState({
    bookImported: false,
    isFirstBook: false,
    readiness: null,
    loaded: false,
  });
  const [cardOffers, setCardOffers] = useState([]);
  const offerStoryId = currentStory?.id || currentSession?.story_id || step0Facts.storyId || null;
  const settledMessageCount = messages.reduce(
    (count, message) => (message?.streaming ? count : count + 1),
    0,
  );
  const summaryMark = summaryStatus?.status || '';

  useEffect(() => {
    if (!offerStoryId) {
      setCardOffers([]);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve()
      .then(() => entitiesApi.listByStory(offerStoryId, null, 'proposed'))
      .then((rows) => {
        if (!cancelled) setCardOffers(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCardOffers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [offerStoryId, settledMessageCount, summaryMark]);
  const step0AskedRef = useRef(false);
  const step0TypingRef = useRef(false);
  const step0OfferedRef = useRef(false);
  const [plotFocus, setPlotFocus] = useState(null);
  const [beatWorkshop, setBeatWorkshop] = useState(null);
  const [sequencePreview, setSequencePreview] = useState(null);
  const [plotPickerOpen, setPlotPickerOpen] = useState(false);
  const [plotGuidedUpdating, setPlotGuidedUpdating] = useState(false);
  const [plotGapUpdating, setPlotGapUpdating] = useState(false);

  // Connect (plot-dossier-spec.md, Focus/Scene sessions): the Connect
  // action links this session to a PlotTrack scene, or creates a new
  // (Unplaced) one from it. Disabled when the story has no outline yet —
  // same "does this story have an outline" signal Story Board itself uses
  // (outline-map's `outline` key existing) and NewSessionDialog.jsx already
  // checks to gate workshop template availability.
  const [hasOutline, setHasOutline] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showSeriesFocusDialog, setShowSeriesFocusDialog] = useState(false);
  const [shapeSaving, setShapeSaving] = useState(false);
  const [showStartBook, setShowStartBook] = useState(false);

  // API Key error handling
  const [apiKeyError, setApiKeyError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsFocus, setSettingsFocus] = useState(null);

  // User settings for read-only mode
  const [userSettings, setUserSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Model selection state (Mature Content Handling feature). The chat model
  // choice itself now lives in ModelPicker (per-function, account-wide —
  // see spec v2 §5); this just tracks the current display name for the
  // moderation alert's copy, fed by ModelPicker's onChange.
  const [chatModelName, setChatModelName] = useState(null);
  const [chatModelId, setChatModelId] = useState(null);
  const [showModerationAlert, setShowModerationAlert] = useState(false);
  const [moderationModelName, setModerationModelName] = useState('');


  // Zoom level for chat text (synced with Settings via localStorage)
  const [zoomLevel, setZoomLevel] = useState(() => (
    readChatZoom(localStorage.getItem(CHAT_TEXT_SIZE.storageKey))
  ));

  // Apply zoom level to document and sync with localStorage.
  // readChatZoom snaps an older percent onto the current steps.
  useEffect(() => {
    if (!Number.isFinite(zoomLevel)) return;
    document.documentElement.style.setProperty('--zoom-level', String(zoomLevel / 100));
    localStorage.setItem(CHAT_TEXT_SIZE.storageKey, String(zoomLevel));
    window.dispatchEvent(new Event('chat-zoom-change'));
  }, [zoomLevel]);

  // Listen for zoom changes from Settings dialog ('storage' covers other
  // tabs; 'chat-zoom-change' covers this tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === CHAT_TEXT_SIZE.storageKey && e.newValue) {
        setZoomLevel(readChatZoom(e.newValue));
      }
    };
    const handleSameTabChange = () => {
      setZoomLevel(readChatZoom(localStorage.getItem(CHAT_TEXT_SIZE.storageKey)));
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('chat-zoom-change', handleSameTabChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('chat-zoom-change', handleSameTabChange);
    };
  }, []);

  // Load user settings on mount
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const response = await settingsApi.getSettings();
        // Unwrap data from { data: {...} } response format
        setUserSettings(unwrapSettings(response));
      } catch (err) {
        console.error('Failed to load user settings:', err);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadUserSettings();
  }, []);

  // A beta key can arrive after the first paint. Keep asking until it does
  // so the writer does not have to reload the page.
  useEffect(() => {
    if (!shouldPollKeyProvisioning(userSettings)) return undefined;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const response = await settingsApi.getSettings();
        if (!cancelled) setUserSettings(unwrapSettings(response));
      } catch (err) {
        console.error('Failed to reload user settings:', err);
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [userSettings?.key_provisioning]);

  // Connect (plot-dossier-spec.md): re-check outline existence whenever the
  // current story changes — the Connect button is disabled with "nothing to
  // connect into" until an outline row exists for this story.
  useEffect(() => {
    let cancelled = false;
    const checkOutline = async () => {
      if (!currentStory?.id) {
        if (!cancelled) setHasOutline(false);
        return;
      }
      try {
        const response = await workshopsApi.getOutlineMap(currentStory.id);
        const data = response?.data || response;
        if (!cancelled) setHasOutline(!!data?.outline);
      } catch (err) {
        if (!cancelled) setHasOutline(false);
      }
    };
    checkOutline();
    return () => { cancelled = true; };
  }, [currentStory?.id]);

  // Reload settings when settings dialog closes
  const handleSettingsClose = async () => {
    setShowSettings(false);
    try {
      const response = await settingsApi.getSettings();
      // Unwrap data from { data: {...} } response format
      setUserSettings(unwrapSettings(response));
    } catch (err) {
      console.error('Failed to reload settings:', err);
    }
  };

  // Chat model changed via ModelPicker (functionKey="chat"). ModelPicker
  // already handles the optimistic save/revert/toast dance itself — this
  // just tracks the display name for the moderation alert and clears any
  // stale alert since the writer just switched models.
  const handleChatModelChange = (modelId, model) => {
    const next = typeof modelId === 'string' && modelId.trim() ? modelId.trim() : null;
    setChatModelId(next);
    setChatModelName(model?.display_name || modelId || null);
    setShowModerationAlert(false);
  };

  const openedMessageRef = useRef('');
  // Handle ?message= and #<message id> for citation and search navigation
  useEffect(() => {
    const messageParam = messageAnchorId(searchParams, location.hash);
    if (messageParam && messages.length > 0 && openedMessageRef.current !== messageParam) {
      openedMessageRef.current = messageParam;
      setHighlightedMessageId(messageParam);

      // Scroll to the message
      setTimeout(() => {
        const element = document.getElementById(`message-${messageParam}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Clear the highlight and URL param after a few seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
        if (searchParams.get('message')) {
          searchParams.delete('message');
          setSearchParams(searchParams, { replace: true });
        }
      }, 5000);
    }
  }, [messages, searchParams, setSearchParams, location.hash]);

  const loadMessages = async () => {
    if (!sessionId) {
      loadGenerationRef.current += 1;
      threadLiveRef.current = false;
      setMessages([]);
      setReplyInFlight(null);
      setWelcomeMessage(null);
      setMessagePage(false, null);
      setIsLoading(false);
      return;
    }

    const requestedSessionId = sessionId;
    const generation = ++loadGenerationRef.current;
    const blockThread = !threadLiveRef.current;
    try {
      if (blockThread) setIsLoading(true);
      setError(null);

      // Fetch messages and session data in parallel. First load after
      // Start this book can miss — retry into the opener (#848).
      const [messagesData, sessionData] = await withChatLoadRetry(() => Promise.all([
        chatApi.getMessages(requestedSessionId),
        sessionsApi.get(requestedSessionId)
      ]));
      const messageList = messagesData?.messages || [];
      const apply = shouldApplyChatMessageLoad({
        generation,
        currentGeneration: loadGenerationRef.current,
        requestedSessionId,
        currentSessionId: sessionIdRef.current,
        incomingMessages: messageList,
        threadLive: threadLiveRef.current,
      });
      if (!apply) return;

      const merged = mergePathGapOpener({
        messages: messageList,
        welcomeMessage: sessionData.welcome_message || null,
        opener: location.state?.pathGapOpener || '',
      });
      setMessages((prev) => {
        const fresh = generation === loadGenerationRef.current;
        // A send already on screen stays. A fresh open of another
        // session still replaces the thread.
        if (fresh && !threadLiveRef.current) return merged.messages;
        return mergeServerThread(prev, merged.messages);
      });
      threadLiveRef.current = true;
      // Seed pagination state from the tail response (issue #202).
      setMessagePage(!!messagesData.has_more, messagesData.next_cursor || null);
      setReplyInFlight(messagesData.reply_in_flight || null);

      // A room thread must not replace the Idea session in the rest of the app.
      if (!pinSession) updateCurrentSession(sessionData);
      setPaneLoadedId(requestedSessionId);

      // RE-ACTIVATED: GuidanceMode for Session Modes feature (January 2026)
      setGuidanceMode(sessionData.guidance_mode || 'explore');
      setFocusTemplate(sessionData.template || null);
      setFocusTargetName(sessionData.focus_target_name || '');
      setMuted(!!sessionData.muted);
      setTeach(!!sessionData.teach);
      setStep0Asked(!!sessionData.step0_asked);
      step0AskedRef.current = !!sessionData.step0_asked;
      setStep0Typing(false);
      step0TypingRef.current = false;
      setStep0Facts({
        storyId: sessionData.story_id || null,
        bookImported: !!sessionData.book_imported,
        isFirstBook: !!sessionData.is_first_book,
        readiness: sessionData.readiness || null,
        loaded: true,
      });
      setSessionType(sessionData.session_type || currentSession?.session_type || null);
      setPlotFocus(sessionData.plot_focus || null);
      setBeatWorkshop(sessionData.beat_workshop || null);
      setSequencePreview(null);

      // If no messages, show the welcome message from the session.
      // Layer B may replace that first line with the unanswered questions.
      setWelcomeMessage(merged.welcomeMessage);

      // Build citation map from messages
      buildCitationMap(messageList);
    } catch (err) {
      if (pendingPaneSendRef.current) {
        pendingPaneSendRef.current = null;
        setIsSending(false);
      }
      // Handle 404 - session was deleted (e.g., project or story was deleted)
      if (err.status === 404) {
        if (generation !== loadGenerationRef.current) return;
        if (pinSession) {
          onPinnedSession?.(null);
          setMessages([]);
          setIsLoading(false);
          return;
        }
        clearCurrentSession();
        navigate('/');
        setToast({
          type: 'info',
          message: 'This session is no longer available.'
        });
        return;
      }
      if (generation === loadGenerationRef.current) {
        setError(err.message || 'Failed to load messages');
      }
    } finally {
      const stillThisSitting = requestedSessionId === sessionIdRef.current;
      const latest = generation === loadGenerationRef.current;
      if (stillThisSitting && blockThread && (latest || threadLiveRef.current)) {
        setIsLoading(false);
      }
    }
  };

  const buildCitationMap = (messageList) => {
    const map = {};
    (messageList || []).forEach((msg) => {
      if (!msg?.id) return;
      const id = String(msg.id);
      const shortId = id.slice(-8);
      map[shortId] = {
        fullId: id,
        preview: asChatText(msg.content).substring(0, 50)
      };
    });
    setCitationMap(map);
  };

  // Lazy-load the next-older batch on scroll-up (issue #202). Prepends older
  // messages and rebuilds the citation map across the full loaded set.
  // `baseMessages` lets a citation click walk pages without waiting for
  // React to publish each prepend.
  const loadOlderMessages = async (baseMessages) => {
    const cursor = messagesCursorRef.current;
    const current = baseMessages || messagesRef.current || [];
    if (!sessionId || !hasMoreMessagesRef.current || olderMessagesInFlightRef.current || !cursor) {
      return current;
    }
    olderMessagesInFlightRef.current = true;
    setIsLoadingOlder(true);
    try {
      const data = await chatApi.getMessages(sessionId, { cursor });
      const older = data.messages || [];
      const merged = older.length > 0 ? [...older, ...current] : current;
      if (older.length > 0) {
        messagesRef.current = merged;
        setMessages(merged);
        buildCitationMap(merged);
      }
      setMessagePage(!!data.has_more, data.next_cursor || null);
      return merged;
    } catch (err) {
      // Non-fatal: keep what's loaded.
      console.error('Failed to load older messages:', err);
      return current;
    } finally {
      olderMessagesInFlightRef.current = false;
      setIsLoadingOlder(false);
    }
  };

  const applyTurnSideEffects = (response) => {
    if (response.session_status) {
      updateSessionStatus(response.session_status);
    }
    if (response.summary_pending) {
      watchBackgroundSummary();
    }
    if (currentStory?.id) {
      window.dispatchEvent(new CustomEvent('graph:pending-count-changed', {
        detail: { storyId: currentStory.id }
      }));
    }
    if (response.continued_in) {
      setContinuationInfo({
        previousSessionId: sessionId,
        newSessionId: response.continued_in
      });
      setShowContinuationModal(true);
      navigate(`/chat/${response.continued_in}`);
      navigateToSession(response.continued_in);
    }
    if (response.debug_data) {
      setLastDebugData(response.debug_data);
    }
    if (response.content_moderation_detected) {
      setModerationModelName(chatModelName || 'the current model');
      setShowModerationAlert(true);
    }
    if (response.foundation?.confirmed) {
      setPendingFoundation(response.foundation);
      setLastAssistantMsgId(response.assistant_message?.id);
    }
    if (response.citation_map) {
      setCitationMap((prev) => ({ ...prev, ...response.citation_map }));
    }
    if (response?.interrupted) {
      setSendNotice(REPLY_STOPPED_EARLY_MESSAGE);
    }
  };

  // Land the streamed turn from the done payload (or the tokens already
  // on screen). Do not call loadMessages() after this — that flip of
  // isLoading unmounts the thread and a stale GET can wipe the reply,
  // leaving an empty typing bubble until a full reload (#947).
  const settleStreamedTurn = (response, { userTempId, streamingId, attempt, keepUser = false }) => {
    if (attempt !== sendAttemptRef.current) return;
    const partial = String(streamPartialRef.current || '').trim();
    if (response?.assistant_message || partial) {
      forgetReplyWait(sessionIdRef.current);
      stallHandledRef.current = true;
    } else if (stallHandledRef.current) {
      return;
    } else {
      // The request finished with nothing to show. Do not keep Send locked.
      forgetReplyWait(sessionIdRef.current);
    }
    if (response?.sequence_preview) {
      setSequencePreview(response.sequence_preview);
    }
    setMessages((prev) => {
      const streamed = prev.find((msg) => msg.id === streamingId);
      const withoutTemps = prev.filter((msg) => {
        if (msg.id === streamingId) return false;
        if (!keepUser && msg.id === userTempId) return false;
        return true;
      });
      const have = new Set(withoutTemps.map((msg) => String(msg.id)));
      const incoming = [];
      if (response.user_message && !have.has(String(response.user_message.id))) {
        incoming.push(response.user_message);
      }
      if (response.assistant_message && !have.has(String(response.assistant_message.id))) {
        incoming.push({
          ...attachBeatWorkshopOffer(
            response.assistant_message,
            response.beat_workshop_offer,
          ),
          interrupted: !!response.interrupted,
        });
      } else if (!response.assistant_message && (partial || (streamed && String(streamed.content || '').trim()))) {
        const kept = streamed && String(streamed.content || '').trim();
        incoming.push({
          ...attachBeatWorkshopOffer(streamed || {
            id: streamingId,
            speaker: 'assistant',
            created_at: new Date().toISOString(),
          }, response.beat_workshop_offer),
          content: kept || partial,
          streaming: false,
          interrupted: !!response.interrupted,
        });
      }
      if (response.appended_messages?.length) {
        incoming.push(...response.appended_messages.filter((msg) => !have.has(String(msg.id))));
      }
      return [...withoutTemps, ...incoming];
    });
  };

  const streamPartialRef = useRef('');

  const handleSendError = (err, { streamingId, userTempId, attempt, keepUser = false } = {}) => {
    // Returns true only when the sent line was removed from the chat.
    if (attempt !== sendAttemptRef.current) return false;
    if (replyInFlightConflict(err)) {
      // This send did not start a generation. The one already running
      // is still the reply to wait for.
      stallHandledRef.current = false;
      setError(null);
      setSendNotice(null);
      setMessages((prev) => prev.filter((msg) => {
        if (msg.id === streamingId) return false;
        if (String(msg.id).startsWith('streaming-')) return false;
        if (userTempId && msg.id === userTempId && String(userTempId).startsWith('temp-')) return false;
        return true;
      }));
      const wait = readReplyWait(sessionIdRef.current);
      setReplyInFlight((current) => (
        current?.started_at
          ? current
          : {
            message_id: wait?.action === 'jump_start' ? 'jump_start' : 'pending',
            started_at: wait?.startedAt || new Date().toISOString(),
          }
      ));
      chatApi.getMessages(sessionIdRef.current).then((data) => {
        if (attempt !== sendAttemptRef.current) return;
        const serverMessages = data?.messages || [];
        if (recoveredAssistant(messagesRef.current, serverMessages)) {
          forgetReplyWait(sessionIdRef.current);
        }
        setReplyInFlight(data?.reply_in_flight || null);
        setMessages((prev) => withoutEmptyReplyWait(mergeServerThread(prev, serverMessages)));
      }).catch(() => {});
      return true;
    }
    if (stallHandledRef.current) return false;
    stallHandledRef.current = true;
    forgetReplyWait(sessionIdRef.current);
    const clearOptimistic = () => {
      setMessages((prev) => prev.filter((msg) => {
        if (msg.id === streamingId) return false;
        if (!keepUser && msg.id === userTempId) return false;
        const id = String(msg.id);
        if (id.startsWith('temp-') || id.startsWith('streaming-')) return false;
        return true;
      }));
    };
    const partial = String(streamPartialRef.current || '').trim();

    if (err.code === 'allowance_used') {
      setSendNotice(null);
      setApiKeyError({
        message: err.message || BETA_ALLOWANCE_USED_MESSAGE,
        type: 'allowance_used',
      });
      clearOptimistic();
      return true;
    }
    if (err.code === 'account_not_ready' || err.code === 'account_blocked') {
      setSendNotice(null);
      setError(err.message || ACCOUNT_READY_MESSAGE);
      clearOptimistic();
      return true;
    }
    if (err.code === 'api_key_not_configured') {
      setApiKeyError({
        message: 'No API key configured. Please add your OpenRouter API key to continue chatting.',
        type: 'not_configured'
      });
      clearOptimistic();
      return true;
    }
    if (err.code === 'insufficient_credits') {
      setApiKeyError(creditsErrorCopy(err));
      clearOptimistic();
      return true;
    }
    if (err.code === 'content_moderation') {
      setApiKeyError({
        message: 'Your content was flagged by the AI provider\'s moderation filter. Try switching to a more permissive model using the dropdown above.',
        type: 'content_moderation'
      });
      clearOptimistic();
      return true;
    }

    const dropStreamingBubble = () => {
      setMessages((prev) => prev.filter((msg) => (
        msg.id !== streamingId && !String(msg.id).startsWith('streaming-')
      )));
    };
    // The turn may already be stored. A failure must not throw away the
    // writer’s line, and a reload has to show the saved reply.
    const revealSavedTurn = () => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      chatApi.getMessages(sid).then((data) => {
        if (attempt !== sendAttemptRef.current) return;
        const serverMessages = data?.messages || [];
        setReplyInFlight(data?.reply_in_flight || null);
        setMessages((prev) => {
          const merged = withoutEmptyReplyWait(mergeServerThread(prev, serverMessages));
          const savedReply = serverMessages.some((msg) => (
            msg?.speaker === 'assistant' && asChatText(msg.content).trim()
          ));
          if (!savedReply) return merged;
          return merged.filter((msg) => !String(msg.id).startsWith('streaming-') && !msg.streaming);
        });
        const savedReply = serverMessages.some((msg) => (
          msg?.speaker === 'assistant' && asChatText(msg.content).trim()
        ));
        if (savedReply) {
          setError(null);
          setSendNotice(null);
          forgetReplyWait(sid);
        }
      }).catch(() => {});
    };

    if (err.code === 'LLM_ERROR' || err.stalled) {
      if (partial && streamingId) {
        setMessages((prev) => prev.map((msg) => (
          msg.id === streamingId
            ? { ...msg, streaming: false, interrupted: true, content: msg.content || partial }
            : msg
        )));
        setSendNotice(REPLY_STOPPED_EARLY_MESSAGE);
      } else {
        dropStreamingBubble();
        setSendNotice(null);
        setError(err.message || REPLY_DID_NOT_ARRIVE_MESSAGE);
      }
      revealSavedTurn();
      return;
    }

    if (unreachableSend(err)) {
      setMessages((prev) => prev.filter((msg) => (
        msg.id !== streamingId && !String(msg.id).startsWith('streaming-')
      )));
      setSendNotice(null);
      setError(null);
      setSendMissed(true);
      return;
    }

    setSendNotice(null);
    setError(err.message || 'Failed to send message');
    dropStreamingBubble();
    revealSavedTurn();
  };

  const handleSendMessage = async (content, { onFailed = null, reuseMessageId = null } = {}) => {
    if (!sessionId) return;
    if (betaAiPaused()) {
      setApiKeyError({
        message: BETA_ALLOWANCE_USED_MESSAGE,
        type: 'allowance_used',
      });
      return;
    }

    const userTempId = reuseMessageId || pendingPaneTempRef.current || `temp-${Date.now()}`;
    pendingPaneTempRef.current = null;
    const streamingId = `streaming-${Date.now()}`;

    const attempt = ++sendAttemptRef.current;
    const listening = muted;
    const answering = !reuseMessageId && step0TypingRef.current && !step0AskedRef.current;
    const dismissing = !reuseMessageId && step0OfferedRef.current && !step0AskedRef.current;
    if (dismissing) {
      step0AskedRef.current = true;
      step0TypingRef.current = false;
      step0OfferedRef.current = false;
      setStep0Asked(true);
      setStep0Typing(false);
      const patch = { step0_asked: true };
      if (answering) patch.step0_answer = true;
      sessionsApi.update(sessionId, patch).catch((err) => {
        setToast({
          message: err.response?.data?.detail || 'Could not save that answer',
          type: 'error',
        });
      });
    }
    try {
      setSendMissed(false);
      setIsSending(true);
      stallHandledRef.current = false;
      // A muted send is not a reply. Remembering a wait makes unmute
      // show the failed-reply line for a message that was kept on purpose.
      if (listening) forgetReplyWait(sessionId);
      else rememberReplyWait(sessionId, 'send');
      setError(null);
      setSendNotice(null);
      streamPartialRef.current = '';
      loadGenerationRef.current += 1;
      threadLiveRef.current = true;
      const userMessage = {
        id: userTempId,
        speaker: 'user',
        content,
        created_at: new Date().toISOString(),
        ...(listening ? { metadata: { heard_while_muted: true } } : {}),
      };
      const streamingMessage = listening ? null : {
        id: streamingId,
        speaker: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        streaming: true
      };
      setMessages((prev) => {
        const hasUser = prev.some((msg) => msg.id === userTempId);
        if (hasUser) return streamingMessage ? [...prev, streamingMessage] : prev;
        // A new send replaces an earlier unsaved failed copy of the same line.
        const base = reuseMessageId ? prev : prev.filter((msg) => !(
          msg.speaker === 'user'
          && String(msg.id).startsWith('temp-')
          && msg.content === content
        ));
        if (!streamingMessage) return [...base, userMessage];
        return [...base, userMessage, streamingMessage];
      });

      const previousSessionId = getPreviousSessionId();
      const response = await chatApi.sendMessageStream(
        sessionId,
        content,
        previousSessionId,
        workingStyle,
        {
          ...(focusSectionId ? { focusSectionId } : {}),
          ...(reuseMessageId ? { retryMessageId: reuseMessageId } : {}),
          ...(answering ? { step0Answer: true } : {}),
          onDelta: (text) => {
            streamPartialRef.current += text;
            setMessages((prev) => prev.map((msg) => (
              msg.id === streamingId
                ? { ...msg, content: `${msg.content}${text}` }
                : msg
            )));
          }
        }
      );

      if (previousSessionId) {
        clearPreviousSession();
      }

      applyTurnSideEffects(response);
      settleStreamedTurn(response, {
        userTempId,
        streamingId,
        attempt,
        keepUser: Boolean(reuseMessageId),
      });
    } catch (err) {
      const removed = handleSendError(err, {
        streamingId,
        userTempId,
        attempt,
        keepUser: Boolean(reuseMessageId),
      });
      if (removed) onFailed?.();
    } finally {
      if (attempt === sendAttemptRef.current) setIsSending(false);
    }
  };

  sendPaneRef.current = handleSendMessage;

  useEffect(() => {
    if (!pane || !sessionId || paneLoadedId !== sessionId) return;
    const text = pendingPaneSendRef.current;
    if (!text) return;
    pendingPaneSendRef.current = null;
    sendPaneRef.current?.(text);
  }, [pane, sessionId, paneLoadedId]);

  // Regenerate the last assistant message in place. Loading/error state lives
  // in ChatMessage itself (matches the per-message action-button pattern);
  // this just makes the API call and replaces the message's content on
  // success, or lets the error propagate back so ChatMessage can show it.
  const handleRegenerate = async (messageId) => {
    if (!sessionId || composerLockedRef.current) return;
    const response = await chatApi.regenerateMessage(sessionId, messageId);
    setMessages((prev) => prev.map((msg) => (
      msg.id === messageId
        ? { ...msg, ...response.assistant_message }
        : msg
    )));
    if (response.citation_map) {
      setCitationMap((prev) => ({ ...prev, ...response.citation_map }));
    }
    if (response.session_status) {
      updateSessionStatus(response.session_status);
    }
    if (response.content_moderation_detected) {
      setModerationModelName(chatModelName || 'the current model');
      setShowModerationAlert(true);
    }
  };

  // Pending paid action. The model runs only from Confirm. Cancel,
  // Escape, and unmount drop this state and spend nothing.
  const [pendingPaid, setPendingPaid] = useState(null);
  const pendingRunRef = useRef(null);

  const openPaid = (actionId, run, extras = {}) => {
    if (betaAiPaused()) {
      pendingRunRef.current = null;
      setPendingPaid({ paused: true });
      return;
    }
    setPendingPaid((current) => {
      if (current) return current;
      pendingRunRef.current = run;
      return { actionId, run, ...extras };
    });
  };

  const cancelPaid = () => {
    pendingRunRef.current = null;
    setPendingPaid(null);
  };

  const confirmPaid = () => {
    const run = pendingRunRef.current;
    pendingRunRef.current = null;
    setPendingPaid(null);
    if (typeof run === 'function') run();
  };

  const handleJumpStart = async () => {
    if (!sessionId || isSending || composerLockedRef.current) return;

    const streamingId = `streaming-${Date.now()}`;
    const attempt = ++sendAttemptRef.current;

    try {
      setIsSending(true);
      stallHandledRef.current = false;
      rememberReplyWait(sessionId, 'jump_start');
      setError(null);
      setSendNotice(null);
      streamPartialRef.current = '';
      loadGenerationRef.current += 1;
      threadLiveRef.current = true;
      setMessages((prev) => [...prev, {
        id: streamingId,
        speaker: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        streaming: true
      }]);

      const previousSessionId = getPreviousSessionId();
      const response = await chatApi.sendMessageStream(
        sessionId,
        '',
        previousSessionId,
        workingStyle,
        {
          jumpStart: true,
          ...(focusSectionId ? { focusSectionId } : {}),
          onDelta: (text) => {
            streamPartialRef.current += text;
            setMessages((prev) => prev.map((msg) => (
              msg.id === streamingId
                ? { ...msg, content: `${msg.content}${text}` }
                : msg
            )));
          }
        }
      );

      if (previousSessionId) {
        clearPreviousSession();
      }

      applyTurnSideEffects(response);
      settleStreamedTurn(response, { streamingId, attempt });
    } catch (err) {
      handleSendError(err, { streamingId, attempt });
      if (attempt === sendAttemptRef.current && !err.code && !err.stalled) {
        setError(err.message || 'Failed to load Jump Start');
      }
    } finally {
      if (attempt === sendAttemptRef.current) setIsSending(false);
    }
  };

  // Nudge (nudge-button-spec.md): mid-session only (message zero is Jump
  // Start's job) and unavailable while muted — both also enforced
  // server-side, this is just the UI-level guard. Unlike Jump Start's
  // button, the response carries a real user_message (the FQ1 marker), so
  // it's appended alongside the assistant batch.
  const handleNudge = async () => {
    if (!sessionId || isSending || composerLockedRef.current || muted || messages.length === 0) return;

    const streamingId = `streaming-${Date.now()}`;
    const attempt = ++sendAttemptRef.current;

    try {
      setIsSending(true);
      stallHandledRef.current = false;
      rememberReplyWait(sessionId, 'send');
      setError(null);
      setSendNotice(null);
      streamPartialRef.current = '';
      loadGenerationRef.current += 1;
      threadLiveRef.current = true;
      setMessages((prev) => [...prev, {
        id: streamingId,
        speaker: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        streaming: true
      }]);

      const previousSessionId = getPreviousSessionId();
      const response = await chatApi.sendMessageStream(
        sessionId,
        '',
        previousSessionId,
        workingStyle,
        {
          nudge: true,
          ...(focusSectionId ? { focusSectionId } : {}),
          onDelta: (text) => {
            streamPartialRef.current += text;
            setMessages((prev) => prev.map((msg) => (
              msg.id === streamingId
                ? { ...msg, content: `${msg.content}${text}` }
                : msg
            )));
          }
        }
      );

      if (previousSessionId) {
        clearPreviousSession();
      }

      applyTurnSideEffects(response);
      settleStreamedTurn(response, { streamingId, attempt });
    } catch (err) {
      handleSendError(err, { streamingId, attempt });
      if (attempt === sendAttemptRef.current && !err.code && !err.stalled) {
        setError(err.message || 'Failed to get a Nudge');
      }
    } finally {
      if (attempt === sendAttemptRef.current) setIsSending(false);
    }
  };

  // Keep / Skip writes votes on the Nudge batch only. It does not
  // delete history or empty the thread on Side Door return.
  const handleNudgeVote = async (messageId, itemId, vote) => {
    if (!sessionId) return;
    let snapshot = null;
    setMessages((prev) => prev.map((msg) => {
      if (String(msg.id) !== String(messageId)) return msg;
      snapshot = msg;
      const metadata = { ...(msg.metadata || {}) };
      metadata.items = (metadata.items || []).map((item) => (
        String(item.id) === String(itemId) ? { ...item, vote } : item
      ));
      return { ...msg, metadata };
    }));
    try {
      const updated = await chatApi.voteNudgeItem(sessionId, messageId, itemId, vote);
      const metadata = updated?.metadata || updated?.data?.metadata;
      if (!metadata) return;
      setMessages((prev) => prev.map((msg) => (
        String(msg.id) === String(messageId) ? { ...msg, metadata } : msg
      )));
    } catch (err) {
      if (snapshot) {
        const prior = snapshot;
        setMessages((prev) => prev.map((msg) => (
          String(msg.id) === String(messageId) ? prior : msg
        )));
      }
      throw err;
    }
  };

  // Mute toggle (mute-button-spec.md). Optimistic — the toggle is the only
  // feedback the writer gets, so it should flip instantly; reverted on
  // failure. The writer controls mute explicitly, never a timed/auto state.
  const handleToggleMute = async () => {
    if (isMuteUpdating) return;
    const next = !muted;
    const previousTeach = teach;
    setMuted(next);
    if (next) {
      setTeach(false);
    }
    if (!sessionId) return;
    setIsMuteUpdating(true);
    try {
      const patch = { muted: next };
      if (next) {
        patch.teach = false;
      }
      await sessionsApi.update(sessionId, patch);
    } catch (err) {
      setMuted(!next);
      if (next) {
        setTeach(previousTeach);
      }
      setToast({
        message: err.response?.data?.detail || 'Failed to update mute state',
        type: 'error'
      });
    } finally {
      setIsMuteUpdating(false);
    }
  };

  const handleToggleTeach = async () => {
    if (isTeachUpdating) return;
    const next = !teach;
    const previousMuted = muted;
    setTeach(next);
    if (next) {
      setMuted(false);
    }
    if (!sessionId) return;
    setIsTeachUpdating(true);
    try {
      const patch = { teach: next };
      if (next) {
        patch.muted = false;
      }
      await sessionsApi.update(sessionId, patch);
    } catch (err) {
      setTeach(!next);
      if (next) {
        setMuted(previousMuted);
      }
      setToast({
        message: err.response?.data?.detail || 'Failed to update Teach',
        type: 'error'
      });
    } finally {
      setIsTeachUpdating(false);
    }
  };

  const isDiscoverySession =
    (sessionType || currentSession?.session_type) === 'discovery';
  const groupedCardOffers = groupCardOffers(messages, cardOffers);
  const looseCardOffers = pane ? [] : groupedCardOffers.loose;
  const removeCardOffer = (entityId) => {
    setCardOffers((prev) => prev.filter((row) => String(row.id) !== String(entityId)));
  };
  const writerTurns = writerTurnCount(messages);
  // #2295: the count includes the line still on its way to the server.
  // Status reads stored writer rows (book_cast._talk / enough_talk), and
  // create_message runs only after this send is already in flight. The
  // count does not change when the partner reply lands (the temp row is
  // swapped for the saved one), so a check at the optimistic count stayed
  // false until a reload. Ask again once that send has finished.
  const castCheckKey = `${writerTurns}:${isSending ? 'sending' : 'idle'}`;
  const castRow = isDiscoverySession && currentStory?.id
    && writerTurns >= CAST_MIN_WRITER_TURNS
    ? (
      <CastOffer
        folded
        storyId={currentStory.id}
        projectId={currentStory.project_id || null}
        checkKey={castCheckKey}
      />
    )
    : null;
  const isEmptyDiscovery = isDiscoverySession && messages.length === 0;
  const hasPathGapOpener = Boolean(location.state?.pathGapOpener);
  // #1417: a book in a series says up front that its chat doesn't read the series.
  const isSeriesBookDiscovery = Boolean(
    currentProject?.is_series && (currentSession?.story_id || currentStory?.id),
  );
  const discoveryEmptyInvitation =
    isEmptyDiscovery && !hasPathGapOpener ? DISCOVERY_EMPTY_INVITATION : null;
  const showStep0 = step0Facts.loaded && shouldOfferStep0({
    isDiscovery: isDiscoverySession,
    isEmpty: messages.length === 0,
    hasPathGapOpener,
    storyId: step0Facts.storyId || currentSession?.story_id || null,
    step0Asked,
    bookImported: step0Facts.bookImported,
    isFirstBook: step0Facts.isFirstBook,
    readiness: step0Facts.readiness,
  });
  step0OfferedRef.current = showStep0 || (step0Typing && !step0Asked);
  step0TypingRef.current = step0Typing;
  step0AskedRef.current = step0Asked;

  const handleStep0Yes = () => {
    step0TypingRef.current = true;
    setStep0Typing(true);
    setStep0Focus((n) => n + 1);
  };

  const handleStep0No = () => {
    step0AskedRef.current = true;
    step0TypingRef.current = false;
    step0OfferedRef.current = false;
    setStep0Asked(true);
    setStep0Typing(false);
    if (!sessionId) return;
    sessionsApi.update(sessionId, { step0_asked: true }).catch((err) => {
      setToast({
        message: err.response?.data?.detail || 'Could not save that answer',
        type: 'error',
      });
    });
  };
  const threadWelcome = (() => {
    if (isEmptyDiscovery && !hasPathGapOpener) return null;
    if (
      typeof welcomeMessage === 'string' &&
      welcomeMessage.includes(RETIRED_DISCOVERY_EMPTY_OPENER)
    ) {
      return null;
    }
    return welcomeMessage;
  })();

  const isPlotFocus = (currentSession?.template || focusTemplate) === 'plot';
  // The room pane is a narrow column. The sequence side panel belongs on
  // the full chat, where it does not squeeze the thread to one letter.
  const showPlotColumn = isPlotFocus && !pane;

  const persistPlotFocus = async (patch) => {
    if (!sessionId) return;
    const updated = await sessionsApi.update(sessionId, patch);
    setPlotFocus(updated.plot_focus || null);
  };

  const handleSelectPlotFramework = async (dto) => {
    setPlotPickerOpen(false);
    try {
      await persistPlotFocus({ plot_framework_ref: storyFrameworkRef(dto) });
    } catch (err) {
      setToast({ message: err.message || 'Could not set the session Framework', type: 'error' });
    }
  };

  const handleClearPlotFramework = async () => {
    try {
      await persistPlotFocus({ plot_framework_declined: true, plot_framework_ref: '' });
    } catch (err) {
      setToast({ message: err.message || 'Could not clear the session Framework', type: 'error' });
    }
  };

  const handleTogglePlotGuided = async () => {
    if (!sessionId || plotGuidedUpdating) return;
    const next = !plotFocus?.guided_mode;
    setPlotFocus((prev) => ({ ...(prev || {}), guided_mode: next }));
    setPlotGuidedUpdating(true);
    try {
      await persistPlotFocus({ plot_guided_mode: next });
    } catch (err) {
      setPlotFocus((prev) => ({ ...(prev || {}), guided_mode: !next }));
      setToast({ message: err.message || 'Could not update guided mode', type: 'error' });
    } finally {
      setPlotGuidedUpdating(false);
    }
  };

  const handleAnalyzePlotGaps = async () => {
    if (!sessionId || plotGapUpdating || !plotFocus?.framework_ref || plotFocus?.declined) return;
    setPlotGapUpdating(true);
    try {
      const updated = await sessionsApi.analyzePlotGaps(sessionId);
      setPlotFocus(updated.plot_focus || null);
    } catch (err) {
      setToast({ message: err.message || 'Could not check coverage', type: 'error' });
    } finally {
      setPlotGapUpdating(false);
    }
  };

  const handleEndSession = async () => {
    if (!sessionId) return;

    try {
      const result = await sessionsApi.end(sessionId);
      setToast({ message: 'Session ended and summarized', type: 'success' });

      if (result.continued_in) {
        // Session was at limit, navigate to continuation
        navigate(`/chat/${result.continued_in}`);
        navigateToSession(result.continued_in);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.detail || 'Failed to end session',
        type: 'error'
      });
    }
  };

  // ── Summary status chip helpers ──────────────────────────────────────
  const clearSummaryTimers = () => {
    if (summaryTimersRef.current.clear) clearTimeout(summaryTimersRef.current.clear);
    if (summaryTimersRef.current.poll) clearTimeout(summaryTimersRef.current.poll);
    summaryTimersRef.current = { clear: null, poll: null };
  };

  const showSummaryStatus = (status, message, autoClearMs = null) => {
    clearSummaryTimers();
    setSummaryStatus({ status, message });
    if (autoClearMs) {
      summaryTimersRef.current.clear = setTimeout(() => setSummaryStatus(null), autoClearMs);
    }
  };

  const dismissSummaryStatus = () => {
    clearSummaryTimers();
    setSummaryStatus(null);
  };

  // Automatic path: the chat response said a background summary was
  // scheduled (summary_pending). There is no push signal for completion,
  // so poll the latest-summary endpoint — only while a summary is known
  // to be pending, every 5s, giving up silently after 1 minute (the
  // backend logs failures; an unresolved chip shouldn't alarm the writer).
  const watchBackgroundSummary = async () => {
    showSummaryStatus('creating', 'Creating a summary…');
    let baseline = null;
    try {
      const latest = await sessionsApi.getLatestInterimSummary(sessionId);
      baseline = latest?.summary_number ?? null;
    } catch (e) {
      baseline = null; // 404 — no summaries yet
    }
    const startedAt = Date.now();
    const deadline = startedAt + 60000;
    const poll = async () => {
      if (Date.now() > deadline) {
        setSummaryStatus(null);
        return;
      }
      try {
        const latest = await sessionsApi.getLatestInterimSummary(sessionId);
        const madeAt = Date.parse(latest?.created_at);
        if (latest && (latest.summary_number !== baseline || madeAt > startedAt)) {
          showSummaryStatus('success', 'Summary updated', 3000);
          return;
        }
      } catch (e) {
        // keep polling — 404 just means not done yet
      }
      summaryTimersRef.current.poll = setTimeout(poll, 5000);
    };
    summaryTimersRef.current.poll = setTimeout(poll, 5000);
  };

  // Cancel timers and hide the chip when switching sessions/unmounting.
  useEffect(() => {
    return () => {
      clearSummaryTimers();
      setSummaryStatus(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // On-demand Create Summary (#1992). Same callbacks as before the
  // one-pane composer dropped the actions block.
  const handleSummaryCreateStart = () => {
    showSummaryStatus('creating', 'Creating a summary…');
  };

  const handleSummaryCreated = () => {
    showSummaryStatus('success', 'Summary updated', 3000);
  };

  const handleSummaryError = (errorMessage) => {
    let friendlyMessage = errorMessage;
    if (errorMessage.includes('Insufficient messages') || errorMessage.includes('Need at least')) {
      friendlyMessage = 'Need more messages before creating a summary. Keep chatting!';
    }
    showSummaryStatus('error', friendlyMessage, 6000);
  };

  const openWriterEditedSummaryReview = () => {
    setSummaryAutoMerge(true);
    setShowSummaryModal(true);
  };

  const revealCitedMessage = (messageId) => {
    setHighlightedMessageId(messageId);
    // Brief delay so the summary modal can close before the scroll.
    setTimeout(() => {
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
    setTimeout(() => {
      setHighlightedMessageId(null);
    }, 5000);
  };

  const messageById = (list, messageId) => (
    (list || []).find((message) => message && String(message.id) === String(messageId))
  );

  // [#N] from a summary. A stored map resolves N to a message id. Rows with
  // no map keep the loaded-array index. Unloaded ids page older via the cursor.
  const SUMMARY_CITATION_PAGE_CAP = 40;
  const handleCitationClick = async (messageNumber, summaryCitationMap) => {
    setShowSummaryModal(false);

    const map = (
      summaryCitationMap
      && typeof summaryCitationMap === 'object'
      && !Array.isArray(summaryCitationMap)
    ) ? summaryCitationMap : null;
    const hasMap = !!(map && Object.keys(map).length > 0);

    if (!hasMap) {
      const loaded = messagesRef.current || [];
      const messageIndex = messageNumber - 1;
      if (messageIndex >= 0 && messageIndex < loaded.length && loaded[messageIndex]?.id) {
        revealCitedMessage(loaded[messageIndex].id);
      } else {
        setToast({ message: `Message #${messageNumber} not found`, type: 'error' });
      }
      return;
    }

    const targetId = map[String(messageNumber)];
    if (targetId == null || targetId === '') {
      setToast({ message: `Message #${messageNumber} not found`, type: 'error' });
      return;
    }

    let loaded = messagesRef.current || [];
    let pages = 0;
    while (
      !messageById(loaded, targetId)
      && hasMoreMessagesRef.current
      && messagesCursorRef.current
      && pages < SUMMARY_CITATION_PAGE_CAP
    ) {
      const beforeLen = loaded.length;
      loaded = await loadOlderMessages(loaded);
      pages += 1;
      if ((loaded || []).length === beforeLen) break;
    }

    const match = messageById(loaded, targetId) || messageById(messagesRef.current, targetId);
    if (match) {
      revealCitedMessage(match.id);
    } else {
      setToast({ message: `Message #${messageNumber} not found`, type: 'error' });
    }
  };

  const handleBookmark = async (messageId) => {
    if (bookmarkFlightRef.current) return;
    const message = messages.find((m) => m.id === messageId);
    if (!message) return;
    bookmarkFlightRef.current = true;
    bookmarkTouchRef.current += 1;
    const existingId = bookmarkedMessages[messageId];
    if (existingId) {
      setBookmarkedMessages((prev) => {
        const updated = { ...prev };
        delete updated[messageId];
        return updated;
      });
      try {
        await bookmarksApi.delete(existingId);
        setToast({ message: 'Bookmark removed', type: 'success' });
        window.dispatchEvent(new CustomEvent('bookmark-removed', {
          detail: { bookmarkId: existingId },
        }));
      } catch (err) {
        setBookmarkedMessages((prev) => ({ ...prev, [messageId]: existingId }));
        setToast({ message: err.message || 'Failed to bookmark message', type: 'error' });
      } finally {
        bookmarkFlightRef.current = false;
      }
      return;
    }

    const pendingId = `pending-${messageId}`;
    setBookmarkedMessages((prev) => ({ ...prev, [messageId]: pendingId }));
    try {
      const titlePreview = message.content.slice(0, 60).trim() + (message.content.length > 60 ? '...' : '');
      const bookmark = await bookmarksApi.bookmarkMessage(sessionId, messageId, {
        user_title: titlePreview,
        category: null
      });
      setBookmarkedMessages((prev) => ({
        ...prev,
        [messageId]: bookmark.id
      }));
      setToast({ message: 'Message bookmarked!', type: 'success' });
      window.dispatchEvent(new CustomEvent('bookmark-created'));
    } catch (err) {
      setBookmarkedMessages((prev) => {
        const updated = { ...prev };
        if (updated[messageId] === pendingId) delete updated[messageId];
        return updated;
      });
      setToast({ message: err.message || 'Failed to bookmark message', type: 'error' });
    } finally {
      bookmarkFlightRef.current = false;
    }
  };

  const handleSelectSession = (selectedSessionId, sessionData = null) => {
    if (pinSession) {
      onPinnedSession?.(selectedSessionId, sessionData);
      return;
    }
    navigateToSession(selectedSessionId, sessionData);
    if (!pane) navigate(`/chat/${selectedSessionId}`);
  };

  // Logline Session Header handlers (for focus mode + logline template)
  const handleLoglineUpdate = (newLogline) => {
    setCurrentLogline(newLogline);
    // Note: The actual API call is handled inside LoglineSessionHeader
  };

  const handleStartOver = async () => {
    if (!sessionId) return;

    try {
      threadLiveRef.current = false;
      setIsLoading(true);
      await sessionsApi.startOver(sessionId);
      setToast({ message: 'Session cleared. Ready for a fresh start!', type: 'success' });
      // Reload messages to show the new welcome message
      await loadMessages();
    } catch (err) {
      console.error('Failed to start over:', err);
      setToast({
        message: err.message || 'Failed to start over. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRememberSeriesShape = async (shape) => {
    if (!currentProject?.id) return;
    setShapeSaving(true);
    try {
      const saved = await seriesGetIdeasApi.rememberShape(currentProject.id, shape);
      setCurrentProject({
        ...currentProject,
        ends_or_continues: saved.ends_or_continues,
        through_line_thing: saved.through_line_thing ?? currentProject.through_line_thing,
      });
    } catch (err) {
      setToast({
        message: err.message || 'Could not remember how this series is built.',
        type: 'error',
      });
    } finally {
      setShapeSaving(false);
    }
  };

  useEffect(() => {
    // A different chat is not the send that locked this composer.
    // An empty room's first send is: the new session is that send.
    if (pendingPaneSendRef.current) return;
    setRestoreComposerFocus((current) => (current ? false : current));
  }, [sessionId]);

  useEffect(() => {
    // Pinning the room session this send just created must not wipe the line.
    // The same seat, with a reply still arriving, must not either: that
    // loading swap unmounted the thread and the reply stopped early.
    const keepSend = Boolean(pane && pendingPaneSendRef.current);
    const streaming = (messagesRef.current || []).some((message) => message?.streaming);
    const sameSeat = Boolean(sessionId) && sessionId === seatedSessionRef.current;
    const keepStream = sameSeat && (streaming || sendingRef.current);
    if (!keepSend && !keepStream) {
      threadLiveRef.current = false;
      if (sessionId) {
        setMessages([]);
        setReplyInFlight(null);
        setWelcomeMessage(null);
        setIsLoading(true);
      }
    }
    seatedSessionRef.current = sessionId || null;
    loadMessages();
    // A room thread must not replace the Idea session.
    if (sessionId && !pinSession) {
      navigateToSession(sessionId);
    }
  }, [sessionId, pinSession]);

  const pendingWait = readReplyWait(sessionId);
  const threadGap = replyGapForThread({
    messages,
    replyInFlight,
    sending: isSending,
    muted,
    pendingWait,
  });
  const replyGap = sendMissed && !isSending && !mutedSilenceTail(messages)
    ? { kind: 'missing', message: REPLY_DID_NOT_ARRIVE_MESSAGE }
    : threadGap;
  const emptyStreaming = (messages || []).some((message) => (
    message?.streaming && !String(message?.content || '').trim()
  ));
  const composerLocked = sendLocked({ sending: isSending, messages, gap: replyGap });
  composerLockedRef.current = composerLocked;

  useEffect(() => {
    if (!isSending || !sessionId || !emptyStreaming) return undefined;
    const attempt = sendAttemptRef.current;
    let timer;
    let stopped = false;

    const look = async () => {
      if (stopped || attempt !== sendAttemptRef.current || stallHandledRef.current) return;
      const stillEmpty = (messagesRef.current || []).some((message) => (
        message?.streaming && !String(message?.content || '').trim()
      ));
      if (!stillEmpty) return;
      let data = null;
      try {
        data = await chatApi.getMessages(sessionIdRef.current);
      } catch {
        data = null;
      }
      if (stopped || attempt !== sendAttemptRef.current) return;
      const landed = data && recoveredAssistant(messagesRef.current, data?.messages || []);
      if (landed) {
        stallHandledRef.current = true;
        forgetReplyWait(sessionIdRef.current);
        setMessages(withoutEmptyReplyWait(
          mergeServerThread(messagesRef.current, data?.messages || []),
        ));
        setReplyInFlight(data?.reply_in_flight || null);
        setError(null);
        if (attempt === sendAttemptRef.current) setIsSending(false);
        return;
      }
      const marker = data?.reply_in_flight;
      const started = marker?.started_at ? new Date(marker.started_at).getTime() : null;
      const age = Number.isFinite(started) ? Date.now() - started : null;
      const stillRunning = Boolean(marker) && (age == null || age < REPLY_FINISH_CAP_MS);
      if (stillRunning) {
        setReplyInFlight(marker);
        timer = setTimeout(look, REPLY_POLL_MS);
        return;
      }
      stallHandledRef.current = true;
      if (marker) setReplyInFlight(marker);
      setMessages((prev) => withoutEmptyReplyWait(prev));
      setIsSending(false);
    };

    timer = setTimeout(look, REPLY_RECOVER_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [isSending, sessionId, emptyStreaming]);

  useEffect(() => {
    if (!sessionId) return undefined;
    const gap = replyGapForThread({
      messages,
      replyInFlight,
      sending: isSending,
      muted,
      pendingWait: readReplyWait(sessionId),
    });
    const stillLooking = gap?.kind === 'writing';
    if (!stillLooking) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      const open = replyGapForThread({
        messages: messagesRef.current,
        replyInFlight,
        sending: isSending,
        muted,
        pendingWait: readReplyWait(sessionId),
      });
      if (open?.kind !== 'writing') {
        setReplyWatch((value) => value + 1);
        return;
      }
      try {
        const data = await chatApi.getMessages(sessionId);
        if (cancelled) return;
        const serverMessages = data?.messages || [];
        if (recoveredAssistant(messagesRef.current, serverMessages)) {
          forgetReplyWait(sessionId);
        }
        setMessages((prev) => withoutEmptyReplyWait(mergeServerThread(prev, serverMessages)));
        setReplyInFlight(data?.reply_in_flight || null);
        setMessagePage(!!data?.has_more, data?.next_cursor || null);
      } catch {
        /* keep the line; the next read tries again */
      }
    }, REPLY_POLL_MS);
    const remain = replyRecoverRemainingMs({
      messages,
      replyInFlight,
      pendingWait: readReplyWait(sessionId),
    });
    const ageTimer = remain != null && remain > 0
      ? setTimeout(() => setReplyWatch((value) => value + 1), remain + 25)
      : null;
    return () => {
      cancelled = true;
      clearInterval(timer);
      if (ageTimer) clearTimeout(ageTimer);
    };
  }, [sessionId, messages, replyInFlight, isSending, muted, replyWatch]);

  const retryMissingReply = () => {
    if (replyGap?.action === 'jump_start') {
      forgetReplyWait(sessionId);
      handleJumpStart();
      return;
    }
    const last = messages[messages.length - 1];
    if (!last || last.speaker !== 'user') return;
    forgetReplyWait(sessionId);
    const savedId = last.id && !String(last.id).startsWith('temp-') ? last.id : null;
    handleSendMessage(last.content, { reuseMessageId: savedId });
  };

  const beginRoomSession = async () => {
    if (!currentStory?.id) throw new Error('Could not open a chat.');
    const payload = {
      guidance_mode: 'explore',
      template: 'open',
    };
    if (roomKey) payload.room_key = roomKey;
    if (roomKey === 'write' && focusSectionId) payload.write_scene_id = focusSectionId;
    const response = await sessionsApi.create(currentStory.id, payload);
    const session = response?.data || response;
    if (!session?.id) throw new Error('Could not open a chat.');
    if (muted || teach) {
      await sessionsApi.update(session.id, {
        muted: Boolean(muted),
        teach: muted ? false : Boolean(teach),
      });
    }
    return session;
  };

  const startBlankRoomChat = async () => {
    if (!pinSession || !roomKey || roomKey === 'idea' || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const session = await beginRoomSession();
      onPinnedSession?.(session.id, session);
    } catch (err) {
      setError(err?.message || 'Could not open a chat.');
    } finally {
      setIsSending(false);
    }
  };

  // Mute, Teach, Get me started, and Nudge. Mute and Teach are one
  // choice. Nudge stays hidden until the thread has a message and Mute
  // is off.
  const openJumpStart = () => {
    if (!sessionId || composerLocked) return;
    openPaid('jump_start', handleJumpStart, {
      inputTokens: threadInputTokens(messages),
      messageCount: messages.length,
      discovery: isDiscoverySession,
    });
  };
  const openNudge = () => {
    if (!sessionId || composerLocked || muted || messages.length === 0) return;
    openPaid('nudge', handleNudge, {
      inputTokens: threadInputTokens(messages),
      messageCount: messages.length,
      discovery: isDiscoverySession,
    });
  };
  const composerHelp = ({
    explainVisible,
    explainDisabled,
    startDisabled,
    hintDisabled,
    showHint,
  }) => (
    <>
      <button
        type="button"
        className={`mute-toggle-btn${muted ? ' mute-toggle-btn--active' : ''}`}
        onClick={handleToggleMute}
        aria-pressed={muted}
        title={muted ? DISCOVERY_CHROME_TITLES.muteOn : DISCOVERY_CHROME_TITLES.muteOff}
      >
        {HELP_JUST_LISTEN}
      </button>
      {explainVisible && (
        <button
          type="button"
          className={`explain-btn${teach ? ' explain-btn--active' : ''}`}
          onClick={handleToggleTeach}
          disabled={explainDisabled}
          aria-pressed={teach}
          title={HELP_EXPLAIN}
        >
          {HELP_EXPLAIN}
        </button>
      )}
      <button
        type="button"
        className="get-started-btn"
        onClick={openJumpStart}
        disabled={startDisabled}
        title={HELP_GET_STARTED}
      >
        {HELP_GET_STARTED}
      </button>
      {showHint && (
        <button
          type="button"
          className="nudge-btn"
          onClick={openNudge}
          disabled={hintDisabled}
          title={DISCOVERY_CHROME_TITLES.nudge}
        >
          {HELP_SMALL_HINT}
        </button>
      )}
    </>
  );

  const chatSubject = chatPaneTitleSubject({ subject, pane, pinned: preferSubject });
  // Room panes: the stepper sits in the pane header, beside Past chats
  // and Hide chat, including a room that has no session yet. Main chat
  // keeps it under the composer — the header row already holds the
  // model picker and the cost gauge.
  const textSizeStepper = (
    <TextSizeStepper
      value={zoomLevel}
      onChange={setZoomLevel}
      min={CHAT_TEXT_SIZE.min}
      max={CHAT_TEXT_SIZE.max}
      step={CHAT_TEXT_SIZE.step}
    />
  );
  const frame = (body) => (
    <ChatPaneFrame
      subject={chatSubject}
      storyId={currentStory?.id || null}
      projectId={currentProject?.id || null}
      storyName={currentStory?.name || ''}
      activeSessionId={sessionId}
      activeSessionName={currentSession?.id === sessionId ? (currentSession?.name || '') : ''}
      onSelectSession={handleSelectSession}
      foldable={pane}
      roomKey={roomKey}
      writeSceneId={roomKey === 'write' ? (focusSectionId || null) : null}
      onNewRoomChat={pinSession && roomKey && roomKey !== 'idea' ? startBlankRoomChat : null}
      headerExtra={pane && !isPhone ? textSizeStepper : null}
    >
      {body}
    </ChatPaneFrame>
  );

  const canSpend = spendAllowed(userSettings);
  // Visible in Idea and in every room pane, including a room that has
  // no thread yet. Without a session the control stays disabled.
  const summaryControls = (
    <>
      <CreateSummaryButton
        sessionId={sessionId}
        disabled={!canSpend}
        onSummaryCreated={handleSummaryCreated}
        onError={handleSummaryError}
        onCreateStart={handleSummaryCreateStart}
        onWriterEditedHead={openWriterEditedSummaryReview}
      />
      <button
        type="button"
        className="chat-interface__summary-btn"
        onClick={() => setShowSummaryModal(true)}
        disabled={!sessionId}
        title="View session summary"
      >
        View Summary
      </button>
    </>
  );

  const sendEstimate = canSpend ? (
    <PaidActionEstimate
      actionId="chat_message"
      inputTokens={threadInputTokens(messages)}
      modelId={chatModelId}
      messageCount={messages.length}
      discovery={isDiscoverySession}
    />
  ) : null;

  const chatToolsToggle = isPhone ? (
    <button
      type="button"
      className="chat-tools-toggle"
      aria-expanded={chatToolsOpen}
      aria-controls="chat-tools-panel"
      onClick={() => setChatToolsOpen((open) => !open)}
    >
      {CHAT_TOOLS}
    </button>
  ) : null;
  const chatToolsPanelProps = isPhone
    ? { id: 'chat-tools-panel', hidden: !chatToolsOpen }
    : {};

  const chatModelPicker = (
    <ModelPicker
      mode="select"
      functionKey="chat"
      onChange={handleChatModelChange}
      disabled={isSending}
    />
  );
  const chatModelControls = (
    <>
      {sendEstimate}
      {chatModelPicker}
    </>
  );

  // If no session selected, show session list
  if (!sessionId) {
    if (!currentStory) {
      return (
        <div className="chat-interface chat-interface--no-story">
          <p>Please select a story to start chatting</p>
        </div>
      );
    }

    const startPaneSession = async (text) => {
      if (!pane || !currentStory?.id || isSending) return;
      const userTempId = `temp-${Date.now()}`;
      pendingPaneSendRef.current = text;
      pendingPaneTempRef.current = userTempId;
      threadLiveRef.current = true;
      setIsSending(true);
      setError(null);
      setMessages((prev) => [...prev, {
        id: userTempId,
        speaker: 'user',
        content: text,
        created_at: new Date().toISOString(),
      }]);
      try {
        const session = await beginRoomSession();
        if (pinSession) onPinnedSession?.(session.id, session);
        else navigateToSession(session.id, session);
      } catch (err) {
        pendingPaneSendRef.current = null;
        pendingPaneTempRef.current = null;
        threadLiveRef.current = false;
        setMessages((prev) => prev.filter((msg) => msg.id !== userTempId));
        setIsSending(false);
        setError(err?.message || 'Failed to send message');
      }
    };

    const emptyHelp = composerHelp({
      explainVisible: true,
      explainDisabled: !spendAllowed(userSettings),
      startDisabled: true,
      hintDisabled: true,
      showHint: !muted && messages.length > 0,
    });

    return frame(
      <div className="chat-interface">
        {pane ? (
          <>
            <p className="chat-pane__menu-empty">
              {roomKey && roomKey !== 'idea' ? ROOM_CHAT_EMPTY : 'Choose a past chat.'}
            </p>
            {error && <p role="alert">{error}</p>}
            <MessageInput
              onSend={(text) => {
                if (!text?.trim() || composerLocked) return false;
                setRestoreComposerFocus(true);
                void startPaneSession(text);
                return true;
              }}
              disabled={composerLocked}
              restoreWhenEnabled={restoreComposerFocus}
              onComposerFocusRestored={() => setRestoreComposerFocus(false)}
            />
            {isPhone && pane && sendEstimate}
            {chatToolsToggle}
            <div className="chat-interface__zoom-row" {...chatToolsPanelProps}>
              {isPhone && textSizeStepper}
              {pane && (
                <div className="chat-pane__model-pair">
                  {isPhone ? chatModelPicker : chatModelControls}
                </div>
              )}
              {emptyHelp}
              {summaryControls}
            </div>
          </>
        ) : (
          <SessionList
            storyId={currentStory.id}
            onSelect={handleSelectSession}
            activeSessionId={null}
          />
        )}
      </div>,
    );
  }

  // Get the medium display name and icon
  const getMediumDisplay = () => {
    if (!currentProject?.medium) return null;
    if (currentProject.medium === 'novel') {
      return { icon: '📖', label: 'Novel' };
    }
    return null;
  };

  const mediumDisplay = getMediumDisplay();
  const blockedComposer = composerBlockedCopy(userSettings, { settingsLoaded });

  const addAttached = async () => {
    const storyId = attachment?.storyId || currentStory?.id;
    if (!attachment?.id || !storyId) {
      setAttachStatus('error');
      return;
    }
    const last = [...messages].reverse().find((message) => {
      const who = String(message?.speaker || message?.role || '').toLowerCase();
      return who === 'assistant' || who === 'ai';
    });
    const text = String(last?.content || '').trim();
    if (!text) {
      setAttachStatus('empty');
      return;
    }
    const asUuid = (value) => (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || ''))
        ? value
        : null
    );
    setAttachStatus('adding');
    try {
      if (attachment.kind === 'note') {
        await storyBibleApi.addNote(
          storyId,
          attachment.id,
          text,
          asUuid(last?.id),
          asUuid(sessionId),
        );
      } else {
        await storyBibleApi.addCards(storyId, [{
          name: attachment.label,
          description: text,
        }], asUuid(last?.id));
      }
      setAttachStatus('added');
    } catch {
      setAttachStatus('error');
    }
  };

  const helpButtons = composerHelp({
    explainVisible: isDiscoverySession || Boolean(pane),
    explainDisabled: !canSpend,
    startDisabled: composerLocked || !canSpend || muted,
    hintDisabled: composerLocked || !canSpend,
    showHint: !muted && messages.length > 0,
  });

  // If session selected, show chat
  return frame(
    <div className="chat-interface">
      {phonePaneControls && (
        <div className="chat-pane__phone-controls">
          <div className="chat-pane__model-pair">
            {chatModelControls}
          </div>
        </div>
      )}
      {!pane && (
      <div className="chat-interface__header-badges">
        <ChatModeLine mode="brainstorm" />
        {mediumDisplay && (
          <div className="chat-interface__medium-badge">
            <span className="medium-badge__icon">{mediumDisplay.icon}</span>
            <span className="medium-badge__label">{mediumDisplay.label}</span>
          </div>
        )}
        {currentStory?.id && (
          <StoryNavigatorBadge
            storyId={currentStory.id}
          />
        )}
        {/* Cost sits immediately left of the one chat model picker. */}
        {headerModel && (
          <div className="chat-interface__header-model">
            {chatModelControls}
          </div>
        )}
      </div>
      )}

      {/* Session-length warnings removed (issue #202): sessions are unbounded. */}

      {/* API Key error banner */}
      {sendNotice && (
        <div className="api-key-error-banner" role="alert">
          <div className="api-key-error-banner__content">
            <span className="api-key-error-banner__message">{sendNotice}</span>
          </div>
        </div>
      )}

      {apiKeyError && (
        <div className={`api-key-error-banner api-key-error-banner--${apiKeyError.type}`}>
          <div className="api-key-error-banner__content">
            <span className="api-key-error-banner__icon">⚠️</span>
            <span className="api-key-error-banner__message">{apiKeyError.message}</span>
          </div>
          <div className="api-key-error-banner__actions">
            {apiKeyError.type === 'not_configured' && (
              <button
                className="api-key-error-banner__action-btn"
                onClick={() => setShowSettings(true)}
              >
                Configure API Key
              </button>
            )}
            {apiKeyError.type === 'content_moderation' && (
              <button
                className="api-key-error-banner__action-btn"
                onClick={() => setApiKeyError(null)}
              >
                Dismiss
              </button>
            )}
            {apiKeyError.type === 'insufficient_credits' && (
              <a
                href="https://openrouter.ai/credits"
                target="_blank"
                rel="noopener noreferrer"
                className="api-key-error-banner__action-btn"
              >
                Add Credits
              </a>
            )}
            {apiKeyError.type === 'allowance_used' && (
              <button
                type="button"
                className="api-key-error-banner__action-btn"
                onClick={() => {
                  if (!openSettingsAtApiKey()) {
                    setSettingsFocus('api-key');
                    setShowSettings(true);
                  }
                }}
              >
                Add your own key
              </button>
            )}
            <button
              className="api-key-error-banner__dismiss"
              onClick={() => setApiKeyError(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Content moderation alert - shown when AI declines due to content (Mature Content Handling feature) */}
      {showModerationAlert && (
        <ContentModerationAlert
          modelName={moderationModelName}
          onDismiss={() => setShowModerationAlert(false)}
        />
      )}

      {isLoading ? (
        <Loading text="Loading messages..." />
      ) : (
        <div
          className={showPlotColumn ? 'chat-interface__plot-split' : undefined}
          style={!showPlotColumn ? { display: 'contents' } : undefined}
        >
        <div
          className={showPlotColumn ? 'chat-interface__plot-main' : undefined}
          style={!showPlotColumn ? { display: 'contents' } : undefined}
        >
          {isPlotFocus && currentStory?.id ? (
            <NarrativePovStrip storyId={currentStory.id} />
          ) : null}
          {/* Logline Header - shown for logline template sessions
              Use LoglineSessionHeader for focus mode (full functionality)
              Use LoglineHeader for explore mode (simpler view) */}
          {currentSession?.template === 'logline' && currentStory?.id && (
            guidanceMode === 'focus' ? (
              <LoglineSessionHeader
                storyId={currentStory.id}
                currentLogline={currentLogline ?? currentStory.log_line}
                onLoglineUpdate={handleLoglineUpdate}
                onStartOver={handleStartOver}
              />
            ) : (
              <LoglineHeader
                storyId={currentStory.id}
                initialLogline={currentStory.log_line}
              />
            )
          )}

          {shouldShowSeriesShapeChooser({
            project: currentProject,
            story: currentStory,
            session: currentSession,
          }) && (
            <>
              <div className="series-start-book" data-testid="start-a-book">
                <button
                  type="button"
                  className="series-start-book__button"
                  onClick={() => setShowStartBook(true)}
                >
                  Start a book
                </button>
                <p className="series-start-book__hint">
                  Start the first book now. How the series is built can wait.
                </p>
              </div>
              {SERIES_PLANNING_TOOLS && (
                <SeriesShapeChooser
                  remembered={currentProject?.ends_or_continues}
                  onChoose={handleRememberSeriesShape}
                  saving={shapeSaving}
                  lede={DISCOVERY_SHAPE_LEDE}
                />
              )}
              <AddBookModal
                isOpen={showStartBook}
                onClose={() => setShowStartBook(false)}
                projectId={currentProject.id}
                initialTitle="Book 1"
                onCreated={({ sessionId }) => {
                  setShowStartBook(false);
                  if (sessionId) navigate(`/chat/${sessionId}`);
                }}
              />
            </>
          )}

          {error ? (
            <ErrorMessage message={error} onRetry={loadMessages} />
          ) : null}
          {showStep0 && (
            <section className="step0-card" aria-labelledby="step0-question" data-testid="step0-card">
              <p id="step0-question" className="step0-card__question">{STEP0_QUESTION}</p>
              {step0Typing && (
                <p className="step0-card__prompt">{STEP0_ANSWER_PROMPT}</p>
              )}
              <div className="step0-card__actions">
                {!step0Typing && (
                  <button type="button" className="step0-card__choice" onClick={handleStep0Yes}>
                    {STEP0_YES}
                  </button>
                )}
                <button type="button" className="step0-card__choice" onClick={handleStep0No}>
                  {STEP0_NO}
                </button>
              </div>
            </section>
          )}
          <ChatErrorBoundary onRetry={loadMessages}>
          <MessageList
            messages={messages}
            welcomeMessage={threadWelcome}
            emptyInvitation={discoveryEmptyInvitation}
            onBookmark={handleBookmark}
            citationMap={citationMap}
            highlightedMessageId={highlightedMessageId}
            bookmarkedMessages={bookmarkedMessages}
            // History pagination (issue #202)
            hasMoreMessages={hasMoreMessages}
            isLoadingOlder={isLoadingOlder}
            onLoadOlder={loadOlderMessages}
            // Session suggestion props
            sessionId={sessionId}
            storyId={currentStory?.id || currentSession?.story_id || step0Facts.storyId || null}
            projectId={currentProject?.id}
            canSaveToSeriesNotes={Boolean(currentProject?.is_series && !currentSession?.story_id)}
            pendingFoundation={pendingFoundation}
            lastAssistantMsgId={lastAssistantMsgId}
            onSuggestionDismissed={() => { setPendingFoundation(null); }}
            onSeriesFocusOfferAccept={() => {
              markSeriesFocusOfferDeclined(sessionId);
              setShowSeriesFocusDialog(true);
            }}
            onPlotReadyOfferAccept={(offer) => {
              markPlotReadyOfferDeclined(sessionId, lastAssistantMsgId);
              if (sessionId) {
                sessionsApi.plotReadyOfferChoice(sessionId, 'opened').catch(() => {});
              }
              navigate(plotReadyOfferPath(
                offer?.story_id || currentStory?.id || currentSession?.story_id,
              ));
            }}
            onPlotReadyOfferDecline={() => {
              if (sessionId) {
                sessionsApi.plotReadyOfferChoice(sessionId, 'skipped').catch(() => {});
              }
            }}
            onBeatWorkshopAccept={async ({ beatName, content, replace, scenes }) => {
              if (!currentStory?.id) {
                throw new Error('Open a book to save this sequence.');
              }
              await plotDossiersApi.acceptWorkshopBeat(currentStory.id, {
                beatName,
                content,
                replace,
                scenes,
              });
              navigate('/dossiers');
            }}
            onSeriesBookCardAccept={async (card) => {
              if (!currentProject?.id) {
                throw new Error('Could not accept this idea note.');
              }
              await persistAcceptedBookIdea(currentProject.id, {
                title: card.title,
                statement: card.proposition || card.statement,
              });
            }}
            onRegenerate={composerLocked ? null : handleRegenerate}
            chatModelId={chatModelId}
            messageCount={messages.length}
            discovery={isDiscoverySession}
            estimateInputTokens={threadInputTokens(messages)}
            onNudgeVote={handleNudgeVote}
            ideaOffersByMessage={groupedCardOffers.byMessage}
            onIdeaOfferResolved={removeCardOffer}
            replyFollow={<ReplyFollow gap={replyGap} onRetry={retryMissingReply} />}
            onNavigateToSession={(newSessionId) => {
              navigateToSession(newSessionId);
              navigate(`/chat/${newSessionId}`);
            }}
          />
          </ChatErrorBoundary>

          {/* Run 11: a series book's Discovery points at Bring into this book
              while the series has people it has not brought in. Not a modal. */}
          {isDiscoverySession && currentStory?.id && currentProject?.is_series
            && (!currentStory.project_id || currentStory.project_id === currentProject.id) && (
            <BringSeriesPeopleOffer storyId={currentStory.id} isSeries variant="card" />
          )}
          {/* Input area with checkpoint button */}
          {/* #2069: cast folds into the same compact row as ideas found. */}
          <div className="chat-note-offers" data-chat-note-offers ref={noteOffersRef}>
            {looseCardOffers.length > 0 ? (
              <ChatIdeaOffers
                offers={looseCardOffers}
                storyId={offerStoryId}
                onResolved={removeCardOffer}
                trailing={castRow}
              />
            ) : castRow}
          </div>
          <div className={`chat-interface__input-area${muted ? ' chat-interface__input-area--muted' : ''}`}>
            <SummaryStatusChip
              status={summaryStatus?.status}
              message={summaryStatus?.message}
              onDismiss={dismissSummaryStatus}
            />
            {muted && (
              <div className="chat-interface__muted-label">
                Muted — Stormy is taking notes
              </div>
            )}
            {pane && attachment && (
              <ChatAttachmentBar
                attachment={attachment}
                onClear={onClearAttachment}
                onAdd={() => { void addAttached(); }}
                status={attachStatus}
              />
            )}
            <MessageInput
              sessionId={sessionId}
              echoText={persistedComposerEcho(messages)}
              onSend={(text, commit) => {
                if (!text?.trim() || composerLocked || !canSpend) return false;
                if (betaAiPaused()) {
                  openPaid('chat_message', () => {});
                  return false;
                }
                setRestoreComposerFocus(true);
                handleSendMessage(text, { onFailed: commit });
                return true;
              }}
              disabled={composerLocked}
              readOnly={!canSpend}
              focusToken={step0Focus}
              restoreWhenEnabled={restoreComposerFocus}
              onComposerFocusRestored={() => setRestoreComposerFocus(false)}
              placeholder={step0Typing && !blockedComposer ? STEP0_ANSWER_PROMPT : blockedComposer?.message}
              showConfigure={blockedComposer ? blockedComposer.showConfigure : false}
              onSettingsClick={blockedComposer?.showConfigure ? () => setShowSettings(true) : undefined}
            />
            {chatToolsToggle}
            <div className="chat-interface__zoom-row" {...chatToolsPanelProps}>
              {(!pane || isPhone) && textSizeStepper}
              {pane && !phonePaneControls && (
                <div className="chat-pane__model-pair">
                  {chatModelControls}
                </div>
              )}
              {helpButtons}
              {!pane && isPlotFocus && (
                <button
                  type="button"
                  className={`mute-toggle-btn${plotFocus?.guided_mode ? ' mute-toggle-btn--active' : ''}`}
                  onClick={handleTogglePlotGuided}
                  disabled={!canSpend || plotGuidedUpdating}
                  aria-pressed={!!plotFocus?.guided_mode}
                  title="When on, Stormy steers toward unaddressed sequences. Off by default."
                >
                  {plotFocus?.guided_mode ? 'Guided' : 'Guide'}
                </button>
              )}
              {!pane && isPlotFocus && (
                <button
                  type="button"
                  className="mute-toggle-btn"
                  onClick={() => {
                    if (!sessionId || plotGapUpdating || !plotFocus?.framework_ref || plotFocus?.declined) return;
                    openPaid('check_coverage', handleAnalyzePlotGaps, {
                      inputTokens: threadInputTokens(messages),
                    });
                  }}
                  disabled={
                    plotGapUpdating
                    || !plotFocus?.framework_ref
                    || plotFocus?.declined
                  }
                  title="Mark which sequences this conversation has covered. Does not change your story."
                >
                  {plotGapUpdating ? 'Checking…' : 'Check coverage'}
                </button>
              )}
              {!pane && guidanceMode === 'focus' && focusTemplate === 'scene' && (
                <button
                  type="button"
                  className="connect-scene-btn"
                  onClick={() => setShowConnectModal(true)}
                  disabled={!hasOutline || isSending || !canSpend}
                  title={hasOutline
                    ? 'Link this session to a Plot Grid scene'
                    : 'No outline yet on Plot Grid — nothing to connect into'}
                >
                  Connect
                </button>
              )}
              {summaryControls}
            </div>
            {isEmptyDiscovery && isSeriesBookDiscovery && (
              <p className="chat-interface__empty-discovery-hint" data-testid="series-book-chat-note">
                {SERIES_BOOK_CHAT_NOTE}
              </p>
            )}
          </div>
        </div>
        {showPlotColumn && (
          <PlotFocusColumn>
            {currentStory?.id ? (
              <SequencePreviewPanel
                storyId={currentStory.id}
                sessionId={sessionId}
                sequenceName={beatWorkshop?.beat_name || focusTargetName || ''}
                serverPreview={sequencePreview}
                messages={messages}
              />
            ) : null}
            <FrameworkReferencePanel
              frameworkRef={plotFocus?.framework_ref}
              storyId={currentStory?.id}
              declined={plotFocus?.declined}
              gapState={plotFocus?.gap_state}
              onPick={() => setPlotPickerOpen(true)}
              onClear={handleClearPlotFramework}
            />
          </PlotFocusColumn>
        )}
        </div>
      )}

      {/* Toast notifications */}
      {pendingPaid?.paused ? (
        <PaidActionConfirm paused onCancel={cancelPaid} />
      ) : pendingPaid ? (
        <PaidActionConfirm
          actionId={pendingPaid.actionId}
          inputTokens={pendingPaid.inputTokens}
          modelId={
            PAID_ACTIONS[pendingPaid.actionId]?.functionKey === 'chat'
              ? chatModelId
              : null
          }
          messageCount={pendingPaid.messageCount}
          discovery={pendingPaid.discovery}
          onCancel={cancelPaid}
          onConfirm={confirmPaid}
        />
      ) : null}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Continuation modal */}
      <ContinuationModal
        isOpen={showContinuationModal}
        onClose={() => setShowContinuationModal(false)}
        newSessionId={continuationInfo?.newSessionId}
        previousSessionId={continuationInfo?.previousSessionId}
      />

      {/* Session summary modal */}
      <SessionSummaryModal
        isOpen={showSummaryModal}
        onClose={() => { setShowSummaryModal(false); setSummaryAutoMerge(false); }}
        sessionId={sessionId}
        onCitationClick={handleCitationClick}
        autoMerge={summaryAutoMerge}
      />

      {/* Settings dialog */}
      <SettingsDialog
        isOpen={showSettings}
        onClose={() => {
          setSettingsFocus(null);
          handleSettingsClose();
        }}
        focusSection={settingsFocus}
      />

      {isPlotFocus && (
        <FrameworkPicker
          isOpen={plotPickerOpen}
          onClose={() => setPlotPickerOpen(false)}
          mode="select"
          hangKind="session"
          selectLabel="Use this Framework"
          selectHint="Brainstorming reference — not saved to your story"
          selectedId={parseStoryFrameworkRef(plotFocus?.framework_ref)?.id || null}
          onSelect={handleSelectPlotFramework}
        />
      )}

      {/* Connect modal (plot-dossier-spec.md, Focus/Scene sessions) */}
      <ConnectSceneModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        storyId={currentStory?.id}
        sessionId={sessionId}
        onConnected={() => setShowConnectModal(false)}
      />

      {showSeriesFocusDialog && currentProject?.id && (
        <NewSessionDialog
          storyId={null}
          projectId={currentProject.id}
          seriesAltitude
          initialPrimaryMode="focus"
          onClose={() => setShowSeriesFocusDialog(false)}
          onSessionCreated={(session) => {
            setShowSeriesFocusDialog(false);
            const newId = session?.id || session?.data?.id;
            if (newId) {
              navigateToSession(newId);
              navigate(`/chat/${newId}`);
            }
          }}
        />
      )}
    </div>
  );
};

export default ChatInterface;
